import { useState } from 'react'
import type { LearningStepTab } from '@/pages/home/aboutLc.content'

interface LcLearningStepBlockProps {
  badge: string
  title: string
  description: string
  note: string
  tabs: LearningStepTab[]
}

export function LcLearningStepBlock({
  badge,
  title,
  description,
  note,
  tabs,
}: LcLearningStepBlockProps) {
  const [active, setActive] = useState(tabs[0]?.value ?? '')
  const current = tabs.find((t) => t.value === active) ?? tabs[0]

  return (
    <section id="learning-step" data-marketing-section="" className="relative w-full bg-qb-primary py-14 lg:py-20 tracking-[-0.6px]">
      {/* Bookshape — white notch pointing down into blue, top center */}
      <img
        src="/marketing/about-lc/bookshape.svg"
        alt=""
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-0 w-[110px] lg:w-[146px] h-auto"
      />

      <div className="mx-auto max-w-[1136px] px-6 lg:px-0 flex flex-col items-center gap-14 lg:gap-20">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 lg:gap-6 text-center w-full">
          <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-white text-qb-primary flex items-center justify-center font-bold text-[20px] lg:text-[24px] leading-[1.4]">
            {badge}
          </div>
          <div className="flex flex-col gap-3 lg:gap-4 items-center">
            <h2 className="font-bold text-white text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2]">
              {title}
            </h2>
            <p className="font-medium lg:font-normal text-white text-[20px] lg:text-[24px] leading-[1.5] lg:leading-[1.4]">
              {description}
            </p>
          </div>
        </div>

        {/* Tabs + content card */}
        <div className="w-full max-w-[360px] lg:max-w-none lg:w-[944px] flex flex-col items-center gap-6 lg:gap-10">
          {/* Mobile tabs — 5 number boxes */}
          <div role="tablist" className="lg:hidden w-full flex items-center justify-between">
            {tabs.map((t, i) => {
              const isActive = t.value === active
              return (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(t.value)}
                  className={
                    'w-14 h-14 rounded-[16px] flex items-center justify-center font-semibold text-[20px] tracking-[-0.6px] transition-colors ' +
                    (isActive
                      ? 'bg-white text-qb-primary'
                      : 'bg-[#5ca8ff] text-white')
                  }
                >
                  {i + 1}
                </button>
              )
            })}
          </div>

          {/* Content card */}
          {current && (
            <div className="w-full bg-white rounded-[24px] shadow-[0px_4px_8px_rgba(0,0,0,0.15)] p-4 pb-6 lg:p-4 lg:pb-8 flex flex-col gap-6 lg:gap-10">
              {/* Desktop tabs — 5 chevron-shaped arrows */}
              <div role="tablist" className="hidden lg:flex h-[120px] w-full rounded-[16px] overflow-hidden">
                {tabs.map((t, i) => {
                  const isActive = t.value === active
                  const isFirst = i === 0
                  const isLast = i === tabs.length - 1
                  const clipPath = isFirst
                    ? 'polygon(0 0, calc(100% - 14px) 0, calc(100% - 14px) calc(50% - 16px), 100% 50%, calc(100% - 14px) calc(50% + 16px), calc(100% - 14px) 100%, 0 100%)'
                    : isLast
                      ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 calc(50% + 16px), 14px 50%, 0 calc(50% - 16px))'
                      : 'polygon(0 0, calc(100% - 14px) 0, calc(100% - 14px) calc(50% - 16px), 100% 50%, calc(100% - 14px) calc(50% + 16px), calc(100% - 14px) 100%, 0 100%, 0 calc(50% + 16px), 14px 50%, 0 calc(50% - 16px))'
                  const inactiveBg = i % 2 === 1 ? 'bg-[#f4f4f4]' : 'bg-[#e5e5e5]'
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActive(t.value)}
                      style={{ clipPath, marginLeft: isFirst ? 0 : -14 }}
                      className={
                        'flex-1 h-full flex flex-col gap-2 items-center justify-center text-center transition-colors relative px-4 ' +
                        (isActive
                          ? 'bg-qb-primary text-white z-10'
                          : `${inactiveBg} text-qb-text-mid`)
                      }
                    >
                      <span className="font-bold text-[16px] leading-[1.5]">
                        {t.step}
                      </span>
                      <span className="font-medium text-[20px] leading-[1.5] whitespace-pre-line text-center w-full">
                        {t.label}
                      </span>
                    </button>
                  )
                })}
              </div>

              {/* Content row — desktop side-by-side, mobile stacked (image on top) */}
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-6 lg:px-4">
                {/* Image — order 1 on mobile, order 2 on desktop */}
                <div className="order-1 lg:order-2 w-full lg:w-[360px] h-[200px] lg:h-[240px] rounded-[16px] overflow-hidden bg-qb-bg-secondary shrink-0">
                  <img
                    src={current.imageUrl}
                    alt={current.label}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Text — order 2 on mobile, order 1 (flex-1) on desktop */}
                <div className="order-2 lg:order-1 flex-1 flex flex-col gap-6">
                  <div className="flex flex-col gap-2 items-center lg:items-start text-center lg:text-left">
                    <p className="font-semibold text-qb-primary text-[18px] lg:text-[20px] leading-[1.5]">
                      {current.step}
                    </p>
                    <h3 className="font-bold text-qb-text-high text-[24px] lg:text-[28px] leading-[1.4]">
                      {current.label}
                    </h3>
                  </div>
                  <ul className="flex flex-col gap-2">
                    {current.bullets.map((b, idx) => (
                      <li
                        key={idx}
                        className="flex gap-2 items-start text-qb-text-mid text-[18px] lg:text-[20px] leading-[1.5]"
                      >
                        <span
                          aria-hidden
                          className="mt-2.5 lg:mt-3 w-1.5 h-1.5 rounded-full bg-qb-text-mid shrink-0"
                        />
                        <span className="flex-1">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Note */}
          <p className="font-normal text-white text-[18px] lg:text-[20px] leading-[1.5] text-center px-2">
            {note}
          </p>
        </div>
      </div>
    </section>
  )
}
